// Sityweb's closed list of business categories (`site.category`), copied
// from polarispartner/sityweb packages/shared/src/site-categories.ts with the
// names prefixed. Part of the create-draft contract: the AI picks one, the
// operator can change it, Sityweb rejects any other value. Whoever changes the
// list on Sityweb copies it here, Sityweb first (ADR 0060).

export const SITYWEB_SITE_CATEGORIES = [
  // Food & Beverage
  "ristorante",
  "pizzeria",
  "trattoria-osteria",
  "pub-birreria",
  "bar-caffetteria",
  "gelateria",
  "pasticceria",
  "panificio-forno",
  "enoteca-vineria",
  "catering",
  "food-truck-chiosco",

  // Alimentari e retail food
  "alimentari",
  "macelleria",
  "pescheria",
  "ortofrutta",
  "supermercato",
  "drogheria-erboristeria",
  "gastronomia",
  "prodotti-tipici",

  // Moda e accessori
  "abbigliamento",
  "calzature",
  "intimo",
  "pelletteria-borse",
  "gioielleria-oreficeria",
  "ottica",
  "profumeria-cosmetica",
  "merceria-tessuti",
  "sartoria-su-misura",

  // Casa, hobby, regalo
  "arredamento-mobili",
  "casalinghi",
  "ferramenta",
  "libreria-cartoleria",
  "fioraio-piante",
  "articoli-regalo",
  "giocattoli",
  "elettronica-informatica",
  "strumenti-musicali",
  "bici-sport",
  "petshop",
  "antiquariato",
  "corniciaio-belle-arti",
  "tabaccheria",
  "combustibili-pellet",

  // Servizi alla persona
  "parrucchiere-barbiere",
  "estetica-nail",
  "centro-massaggi",
  "tatuaggi-piercing",
  "lavanderia-tintoria",
  "calzolaio",
  "riparazione-elettronica",
  "riparazione-elettrodomestici",

  // Wellness e sport
  "palestra-fitness",
  "yoga-pilates",
  "scuola-danza",
  "arti-marziali",
  "piscina-nuoto",
  "spa-benessere",
  "centro-dimagrimento",
  "centro-equestre",

  // Salute
  "studio-dentistico",
  "studio-medico-poliambulatorio",
  "fisioterapia-osteopatia",
  "psicologo-psicoterapeuta",
  "veterinario",
  "farmacia-parafarmacia",
  "ortopedia-protesi",
  "nutrizionista",
  "logopedia-riabilitazione",
  "casa-riposo",

  // Servizi professionali
  "studio-legale",
  "commercialista-consulente-lavoro",
  "notaio",
  "architetto",
  "ingegnere-geometra",
  "agenzia-immobiliare",
  "agenzia-assicurazioni",
  "agenzia-viaggi",
  "agenzia-pubblicitaria",
  "agenzia-finanziaria",
  "web-agency",
  "consulenza-aziendale",
  "formazione-corsi",
  "scuola-lingue",
  "scuola-privata-paritaria",
  "traduzioni-interpretariato",

  // Casa e impianti
  "idraulico-termoidraulico",
  "elettricista",
  "climatizzazione",
  "fotovoltaico",
  "fabbro-serramentista",
  "vetraio",
  "imbianchino",
  "muratore-edile",
  "impresa-costruzioni-ristrutturazioni",
  "piastrellista-pavimenti",
  "giardiniere-manutenzione-verde",
  "disinfestazione",
  "pulizie",
  "traslochi",
  "antifurto-sicurezza",

  // Automotive e nautica
  "officina-auto",
  "carrozzeria",
  "gommista",
  "autolavaggio",
  "elettrauto",
  "concessionario-auto",
  "autoricambi",
  "scuola-guida",
  "centro-revisioni-auto",
  "noleggio-auto",
  "officina-moto",
  "nautica-imbarcazioni",

  // Ospitalità e turismo
  "hotel",
  "bed-breakfast",
  "agriturismo",
  "casa-vacanze-affittacamere",
  "ostello-camping",
  "resort-villaggio",
  "stabilimento-balneare",
  "guida-turistica",

  // Eventi e foto/video
  "fotografo",
  "video-maker",
  "wedding-planner",
  "organizzazione-eventi",
  "noleggio-attrezzature-eventi",
  "musicisti-band-dj",
  "location-eventi",

  // Agricoltura e produzione
  "azienda-agricola",
  "cantina-vinicola",
  "frantoio",
  "caseificio",
  "birrificio-artigianale",
  "apicoltura",
  "vivaio",
  "allevamento",

  // Artigianato e industria
  "artigianato-generico",
  "falegnameria",
  "carpenteria-metallica",
  "tipografia-stampa",
  "officina-meccanica-industriale",

  // Trasporti
  "trasporti-merci",
  "corriere-logistica",
  "taxi-ncc",

  // Animali
  "toelettatura-animali",
  "pensione-animali",
  "addestramento-cani",

  // Utilities
  "fornitore-energia-gas",

  // Altro
  "onoranze-funebri",
  "associazione-no-profit",
  "centro-culturale",
  "museo-galleria",
  "altro",
] as const;

export type SitywebSiteCategory = (typeof SITYWEB_SITE_CATEGORIES)[number];

export const SITYWEB_SITE_CATEGORY_LABELS: Record<SitywebSiteCategory, string> = {
  ristorante: "Ristorante",
  pizzeria: "Pizzeria",
  "trattoria-osteria": "Trattoria, osteria",
  "pub-birreria": "Pub, birreria",
  "bar-caffetteria": "Bar, caffetteria",
  gelateria: "Gelateria",
  pasticceria: "Pasticceria",
  "panificio-forno": "Panificio, forno",
  "enoteca-vineria": "Enoteca, vineria",
  catering: "Catering",
  "food-truck-chiosco": "Food truck, chiosco",

  alimentari: "Alimentari",
  macelleria: "Macelleria",
  pescheria: "Pescheria",
  ortofrutta: "Ortofrutta",
  supermercato: "Supermercato",
  "drogheria-erboristeria": "Drogheria, erboristeria",
  gastronomia: "Gastronomia",
  "prodotti-tipici": "Prodotti tipici",

  abbigliamento: "Abbigliamento (donna, uomo, bambino, sportivo)",
  calzature: "Calzature",
  intimo: "Intimo",
  "pelletteria-borse": "Pelletteria, borse",
  "gioielleria-oreficeria": "Gioielleria, oreficeria",
  ottica: "Ottica",
  "profumeria-cosmetica": "Profumeria, cosmetica",
  "merceria-tessuti": "Merceria, tessuti",
  "sartoria-su-misura": "Sartoria su misura",

  "arredamento-mobili": "Arredamento, mobili",
  casalinghi: "Casalinghi",
  ferramenta: "Ferramenta",
  "libreria-cartoleria": "Libreria, cartoleria",
  "fioraio-piante": "Fioraio, piante",
  "articoli-regalo": "Articoli regalo",
  giocattoli: "Giocattoli",
  "elettronica-informatica": "Elettronica, informatica",
  "strumenti-musicali": "Strumenti musicali",
  "bici-sport": "Bici, articoli sportivi",
  petshop: "Petshop",
  antiquariato: "Antiquariato",
  "corniciaio-belle-arti": "Corniciaio, belle arti",
  tabaccheria: "Tabaccheria",
  "combustibili-pellet": "Combustibili, pellet",

  "parrucchiere-barbiere": "Parrucchiere, barbiere",
  "estetica-nail": "Estetica, nail center",
  "centro-massaggi": "Centro massaggi",
  "tatuaggi-piercing": "Tatuaggi, piercing",
  "lavanderia-tintoria": "Lavanderia, tintoria",
  calzolaio: "Calzolaio",
  "riparazione-elettronica": "Riparazione elettronica (cellulari, PC, tablet)",
  "riparazione-elettrodomestici": "Riparazione elettrodomestici",

  "palestra-fitness": "Palestra, fitness, crossfit",
  "yoga-pilates": "Yoga, pilates",
  "scuola-danza": "Scuola di danza",
  "arti-marziali": "Arti marziali",
  "piscina-nuoto": "Piscina, nuoto",
  "spa-benessere": "SPA, centro benessere",
  "centro-dimagrimento": "Centro dimagrimento",
  "centro-equestre": "Centro equestre, maneggio",

  "studio-dentistico": "Studio dentistico",
  "studio-medico-poliambulatorio": "Studio medico, poliambulatorio",
  "fisioterapia-osteopatia": "Fisioterapia, osteopatia",
  "psicologo-psicoterapeuta": "Psicologo, psicoterapeuta",
  veterinario: "Veterinario",
  "farmacia-parafarmacia": "Farmacia, parafarmacia",
  "ortopedia-protesi": "Ortopedia, protesi",
  nutrizionista: "Nutrizionista",
  "logopedia-riabilitazione": "Logopedia, riabilitazione",
  "casa-riposo": "Casa di riposo",

  "studio-legale": "Studio legale",
  "commercialista-consulente-lavoro": "Commercialista, consulente del lavoro",
  notaio: "Notaio",
  architetto: "Architetto",
  "ingegnere-geometra": "Ingegnere, geometra",
  "agenzia-immobiliare": "Agenzia immobiliare",
  "agenzia-assicurazioni": "Agenzia assicurazioni",
  "agenzia-viaggi": "Agenzia viaggi",
  "agenzia-pubblicitaria": "Agenzia pubblicitaria",
  "agenzia-finanziaria": "Agenzia finanziaria, prestiti",
  "web-agency": "Web agency",
  "consulenza-aziendale": "Consulenza aziendale",
  "formazione-corsi": "Formazione, corsi",
  "scuola-lingue": "Scuola di lingue",
  "scuola-privata-paritaria": "Scuola privata, paritaria",
  "traduzioni-interpretariato": "Traduzioni, interpretariato",

  "idraulico-termoidraulico": "Idraulico, termoidraulico",
  elettricista: "Elettricista",
  climatizzazione: "Climatizzazione",
  fotovoltaico: "Fotovoltaico",
  "fabbro-serramentista": "Fabbro, serramentista",
  vetraio: "Vetraio",
  imbianchino: "Imbianchino",
  "muratore-edile": "Muratore, edile",
  "impresa-costruzioni-ristrutturazioni": "Impresa costruzioni, ristrutturazioni",
  "piastrellista-pavimenti": "Piastrellista, pavimenti",
  "giardiniere-manutenzione-verde": "Giardiniere, manutenzione verde",
  disinfestazione: "Disinfestazione",
  pulizie: "Pulizie",
  traslochi: "Traslochi",
  "antifurto-sicurezza": "Antifurto, sicurezza",

  "officina-auto": "Officina auto",
  carrozzeria: "Carrozzeria",
  gommista: "Gommista",
  autolavaggio: "Autolavaggio",
  elettrauto: "Elettrauto",
  "concessionario-auto": "Concessionario auto",
  autoricambi: "Autoricambi",
  "scuola-guida": "Scuola guida",
  "centro-revisioni-auto": "Centro revisioni auto",
  "noleggio-auto": "Noleggio auto",
  "officina-moto": "Officina moto",
  "nautica-imbarcazioni": "Nautica, imbarcazioni",

  hotel: "Hotel",
  "bed-breakfast": "Bed & Breakfast",
  agriturismo: "Agriturismo",
  "casa-vacanze-affittacamere": "Casa vacanze, affittacamere",
  "ostello-camping": "Ostello, camping",
  "resort-villaggio": "Resort, villaggio",
  "stabilimento-balneare": "Stabilimento balneare",
  "guida-turistica": "Guida turistica",

  fotografo: "Fotografo",
  "video-maker": "Video maker",
  "wedding-planner": "Wedding planner",
  "organizzazione-eventi": "Organizzazione eventi",
  "noleggio-attrezzature-eventi": "Noleggio attrezzature eventi",
  "musicisti-band-dj": "Musicisti, band, DJ",
  "location-eventi": "Location per eventi",

  "azienda-agricola": "Azienda agricola",
  "cantina-vinicola": "Cantina vinicola",
  frantoio: "Frantoio",
  caseificio: "Caseificio",
  "birrificio-artigianale": "Birrificio artigianale",
  apicoltura: "Apicoltura",
  vivaio: "Vivaio",
  allevamento: "Allevamento",

  "artigianato-generico": "Artigianato",
  falegnameria: "Falegnameria",
  "carpenteria-metallica": "Carpenteria metallica",
  "tipografia-stampa": "Tipografia, stampa",
  "officina-meccanica-industriale": "Officina meccanica industriale",

  "trasporti-merci": "Trasporti merci",
  "corriere-logistica": "Corriere, logistica, magazzino",
  "taxi-ncc": "Taxi, NCC",

  "toelettatura-animali": "Toelettatura animali",
  "pensione-animali": "Pensione animali",
  "addestramento-cani": "Addestramento cani",

  "fornitore-energia-gas": "Fornitore energia, gas",

  "onoranze-funebri": "Onoranze funebri",
  "associazione-no-profit": "Associazione, no profit",
  "centro-culturale": "Centro culturale",
  "museo-galleria": "Museo, galleria",
  altro: "Altro",
};

export const SITYWEB_SITE_CATEGORY_GROUPS: ReadonlyArray<{
  label: string;
  categories: ReadonlyArray<SitywebSiteCategory>;
}> = [
  {
    label: "Food & Beverage",
    categories: [
      "ristorante",
      "pizzeria",
      "trattoria-osteria",
      "pub-birreria",
      "bar-caffetteria",
      "gelateria",
      "pasticceria",
      "panificio-forno",
      "enoteca-vineria",
      "catering",
      "food-truck-chiosco",
    ],
  },
  {
    label: "Alimentari e retail food",
    categories: [
      "alimentari",
      "macelleria",
      "pescheria",
      "ortofrutta",
      "supermercato",
      "drogheria-erboristeria",
      "gastronomia",
      "prodotti-tipici",
    ],
  },
  {
    label: "Moda e accessori",
    categories: [
      "abbigliamento",
      "calzature",
      "intimo",
      "pelletteria-borse",
      "gioielleria-oreficeria",
      "ottica",
      "profumeria-cosmetica",
      "merceria-tessuti",
      "sartoria-su-misura",
    ],
  },
  {
    label: "Casa, hobby, regalo",
    categories: [
      "arredamento-mobili",
      "casalinghi",
      "ferramenta",
      "libreria-cartoleria",
      "fioraio-piante",
      "articoli-regalo",
      "giocattoli",
      "elettronica-informatica",
      "strumenti-musicali",
      "bici-sport",
      "petshop",
      "antiquariato",
      "corniciaio-belle-arti",
      "tabaccheria",
      "combustibili-pellet",
    ],
  },
  {
    label: "Servizi alla persona",
    categories: [
      "parrucchiere-barbiere",
      "estetica-nail",
      "centro-massaggi",
      "tatuaggi-piercing",
      "lavanderia-tintoria",
      "calzolaio",
      "riparazione-elettronica",
      "riparazione-elettrodomestici",
    ],
  },
  {
    label: "Wellness e sport",
    categories: [
      "palestra-fitness",
      "yoga-pilates",
      "scuola-danza",
      "arti-marziali",
      "piscina-nuoto",
      "spa-benessere",
      "centro-dimagrimento",
      "centro-equestre",
    ],
  },
  {
    label: "Salute",
    categories: [
      "studio-dentistico",
      "studio-medico-poliambulatorio",
      "fisioterapia-osteopatia",
      "psicologo-psicoterapeuta",
      "veterinario",
      "farmacia-parafarmacia",
      "ortopedia-protesi",
      "nutrizionista",
      "logopedia-riabilitazione",
      "casa-riposo",
    ],
  },
  {
    label: "Servizi professionali",
    categories: [
      "studio-legale",
      "commercialista-consulente-lavoro",
      "notaio",
      "architetto",
      "ingegnere-geometra",
      "agenzia-immobiliare",
      "agenzia-assicurazioni",
      "agenzia-viaggi",
      "agenzia-pubblicitaria",
      "agenzia-finanziaria",
      "web-agency",
      "consulenza-aziendale",
      "formazione-corsi",
      "scuola-lingue",
      "scuola-privata-paritaria",
      "traduzioni-interpretariato",
    ],
  },
  {
    label: "Casa e impianti",
    categories: [
      "idraulico-termoidraulico",
      "elettricista",
      "climatizzazione",
      "fotovoltaico",
      "fabbro-serramentista",
      "vetraio",
      "imbianchino",
      "muratore-edile",
      "impresa-costruzioni-ristrutturazioni",
      "piastrellista-pavimenti",
      "giardiniere-manutenzione-verde",
      "disinfestazione",
      "pulizie",
      "traslochi",
      "antifurto-sicurezza",
    ],
  },
  {
    label: "Automotive e nautica",
    categories: [
      "officina-auto",
      "carrozzeria",
      "gommista",
      "autolavaggio",
      "elettrauto",
      "concessionario-auto",
      "autoricambi",
      "scuola-guida",
      "centro-revisioni-auto",
      "noleggio-auto",
      "officina-moto",
      "nautica-imbarcazioni",
    ],
  },
  {
    label: "Ospitalità e turismo",
    categories: [
      "hotel",
      "bed-breakfast",
      "agriturismo",
      "casa-vacanze-affittacamere",
      "ostello-camping",
      "resort-villaggio",
      "stabilimento-balneare",
      "guida-turistica",
    ],
  },
  {
    label: "Eventi e foto/video",
    categories: [
      "fotografo",
      "video-maker",
      "wedding-planner",
      "organizzazione-eventi",
      "noleggio-attrezzature-eventi",
      "musicisti-band-dj",
      "location-eventi",
    ],
  },
  {
    label: "Agricoltura e produzione",
    categories: [
      "azienda-agricola",
      "cantina-vinicola",
      "frantoio",
      "caseificio",
      "birrificio-artigianale",
      "apicoltura",
      "vivaio",
      "allevamento",
    ],
  },
  {
    label: "Artigianato e industria",
    categories: [
      "artigianato-generico",
      "falegnameria",
      "carpenteria-metallica",
      "tipografia-stampa",
      "officina-meccanica-industriale",
    ],
  },
  {
    label: "Trasporti",
    categories: ["trasporti-merci", "corriere-logistica", "taxi-ncc"],
  },
  {
    label: "Animali",
    categories: ["toelettatura-animali", "pensione-animali", "addestramento-cani"],
  },
  {
    label: "Utilities",
    categories: ["fornitore-energia-gas"],
  },
  {
    label: "Altro",
    categories: [
      "onoranze-funebri",
      "associazione-no-profit",
      "centro-culturale",
      "museo-galleria",
      "altro",
    ],
  },
];

export function isSitywebSiteCategory(s: unknown): s is SitywebSiteCategory {
  return typeof s === "string" && (SITYWEB_SITE_CATEGORIES as readonly string[]).includes(s);
}
